import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";

import { chemicalInventoryApi } from "../../../../../api/chemical-inventory";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../../components/dashboard/dashboardHeader";
import { PMDialog } from "../../../../../components/utils/PMDialog";
import { ModuleFilter } from "../../../../../components/utils/ModuleFilter";
import { ChemicalInventoryForm } from "../../../../../components/dashboard/quality/chemical-inventory-form";

/**
 * HSE/KS Chemical Inventory List - Stoffkartotek (ISO 14001, ISO 45001)
 */
export const HseKsChemicalInventoryList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [chemicals, setChemicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    hms_level: "",
    status: "",
    brand_id: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedChemical, setSelectedChemical] = useState(null);

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.includes("/matrix")) return "matrix";
    if (path.includes("/sja")) return "sja";
    if (path.includes("/climate")) return "climate";
    if (path.includes("/chemical-inventory")) return "chemical-inventory";
    if (path.includes("/protective-equipment")) return "protective-equipment";
    return "register";
  });

  const tabs = [
    { id: "register", label: "Risikoregister", path: "/dashboard/hse-ks/risk/register" },
    { id: "matrix", label: "Risikomatrise", path: "/dashboard/hse-ks/risk/matrix" },
    { id: "sja", label: "SJA", path: "/dashboard/hse-ks/risk/sja" },
    { id: "climate", label: "Klimarisiko", path: "/dashboard/hse-ks/risk/climate" },
    { id: "chemical-inventory", label: "Stoffkartotek", path: "/dashboard/hse-ks/risk/chemical-inventory" },
    { id: "protective-equipment", label: "Vernerunder", path: "/dashboard/hse-ks/risk/protective-equipment" },
  ];

  const fetchChemicals = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page,
        per_page: pagination.per_page,
      };
      const response = await chemicalInventoryApi.getChemicalInventories(
        params
      );
      setChemicals(response.data || []);
      setPagination({
        ...pagination,
        page,
        total: response.meta?.total || 0,
        total_pages: response.meta?.total_pages || 0,
      });
    } catch (err) {
      console.error("Error fetching chemical inventories:", err);
      setError(true);
      toast.error(t("Kunne ikke laste stoffkartotek"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChemicals();
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page) => {
    fetchChemicals(page);
  };

  const handleCreateChemical = async (chemicalData) => {
    try {
      // This would be handled by the form component
      setShowCreateDialog(false);
      toast.success(t("Stoffkartotek ble opprettet"));
      fetchChemicals(1);
    } catch (err) {
      toast.error(t("Kunne ikke opprette stoffkartotek"));
    }
  };

  const handleDeleteChemical = async (chemicalId) => {
    if (window.confirm(t("Er du sikker på at du vil slette dette stoffet?"))) {
      try {
        await chemicalInventoryApi.deleteChemicalInventory(chemicalId);
        toast.success(t("Stoffkartotek ble slettet"));
        fetchChemicals(pagination.page);
      } catch (err) {
        toast.error(t("Kunne ikke slette stoffkartotek"));
      }
    }
  };

  const getHmsLevelIcon = (level) => {
    switch (level) {
      case 1:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 2:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 3:
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 4:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <BeakerIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "active":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.status.active")}
          </span>
        );
      case "inactive":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("chemical.inventory.status.inactive")}
          </span>
        );
      case "archived":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.status.archived")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getHmsLevelBadge = (level) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (level) {
      case 1:
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("chemical.inventory.hms_level.1")}
          </span>
        );
      case 2:
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("chemical.inventory.hms_level.2")}
          </span>
        );
      case 3:
        return (
          <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
            {t("chemical.inventory.hms_level.3")}
          </span>
        );
      case 4:
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            {t("chemical.inventory.hms_level.4")}
          </span>
        );
      default:
        return null;
    }
  };

  if (loading && chemicals.length === 0) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={["Kunne ikke laste stoffkartotek"]} />;
  }

  return (
    <>
      <DashboardHeader
        title="Stoffkartotek"
        subtitle="Oversikt over kjemikalier og stoffer (ISO 14001, ISO 45001)"
        mainButton={{
          label: t("chemical.inventory.new"),
          action: () => setShowCreateDialog(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Navigation Tabs */}
          <div className="bg-white shadow sm:rounded-lg mb-6">
            <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
              <nav className="flex space-x-4 overflow-x-auto" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      navigate(tab.path);
                    }}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

        {/* Filters Section */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("chemical.inventory.search")}
                  value={filters.search}
                  onChange={(e) =>
                    handleFilterChange({ ...filters, search: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                {t("Filtre")}
              </button>
            </div>
          </div>

          {showFilters && (
            <ModuleFilter
              onFiltersChange={handleFilterChange}
              initialFilters={filters}
              acceptedStatuses={["active", "inactive", "archived"]}
              searchPlaceholder={t("chemical.inventory.search")}
              filterTitle={t("Filter by Status")}
            />
          )}
        </div>

        {/* Chemicals List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {!chemicals || chemicals.length === 0 ? (
            <ResourceEmpty message={t("chemical.inventory.no_results")} />
          ) : (
            <ul className="divide-y divide-gray-200">
              {chemicals.map((chemical) => (
                <li key={chemical.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {getHmsLevelIcon(chemical.hms_level)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {chemical.name}
                          </p>
                          {getStatusBadge(chemical.status)}
                          {getHmsLevelBadge(chemical.hms_level)}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>
                            {t("chemical.inventory.cas_number")}:{" "}
                            {chemical.cas_number}
                          </span>
                          <span>
                            {t("chemical.inventory.version")}:{" "}
                            {chemical.version}
                          </span>
                          <span>{chemical.brand?.name || "Ingen merke"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/dashboard/hse-ks/risk/chemical-inventory/${chemical.id}`}
                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        {t("Vis")}
                      </Link>
                      <button
                        onClick={() => {
                          setSelectedChemical(chemical);
                          setShowCreateDialog(true);
                        }}
                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        {t("Rediger")}
                      </button>
                      <button
                        onClick={() => handleDeleteChemical(chemical.id)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        {t("Slett")}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t("Viser")} {(pagination.page - 1) * pagination.per_page + 1} -{" "}
              {Math.min(
                pagination.page * pagination.per_page,
                pagination.total
              )}{" "}
              {t("av")} {pagination.total} {t("resultater")}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("Forrige")}
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("Neste")}
              </button>
            </div>
          </div>
        )}
        </div>
      </DashboardHeader>

      {/* Create/Edit Dialog */}
      <ChemicalInventoryForm
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setSelectedChemical(null);
        }}
        chemicalInventory={selectedChemical}
        onSuccess={() => {
          fetchChemicals(pagination.page);
          setShowCreateDialog(false);
          setSelectedChemical(null);
        }}
      />
    </>
  );
};
